import React, { useMemo, useState } from 'react'
import { Alert, Button, Flex } from 'antd'
import { PageHeader } from '@pushly/aqe/lib/components'
import { useService } from '@pushly/aqe/lib/hooks'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { GetAccountResponse } from '@pushly/models/lib/structs/accounts'
import { AppState } from '../../stores/app'
import { VERTICAL_WELL_GAP } from '../../theme/constants'
import { LoadingAnnouncement } from './components/loading-announcement'
import { SectionError } from './components/section-error'
import { TableSkeleton } from './components/table-skeleton'
import { DashboardTableCard } from './components/dashboard-table-card'
import { DomainSearch } from './components/domain-search'
import { TableSectionWell } from './components/section-well'
import { TABLE_PLACEHOLDER_ROW_COUNT } from './components/table-placeholder-rows'
import { useOrgDashboardDomains } from './data/use-org-dashboard-domains'
import { DashboardDomain } from './types'
import { NotificationRankingChoice, useOrgDashboardRankings } from './data/use-org-dashboard-rankings'
import { useOrgDashboardRegistry } from './data/use-org-dashboard-registry'
import { useOrgDashboardSeries } from './data/use-org-dashboard-series'
import { isTopNotificationsWithheld } from './data/withheld-measures'
import { latestCompleteDateAcross } from './math/windows'
import { useOrgDashboardQuery } from './query/use-org-dashboard-query'
import { DateRangeControl } from './sections/controls/date-range-control'
import { ComparisonSummary } from './sections/controls/comparison-summary'
import { DomainFilterControl } from './sections/controls/domain-filter-control'
import { RefreshButton } from './sections/controls/refresh-button'
import { AcquisitionSection } from './sections/acquisition/acquisition-section'
import { ContentSection } from './sections/content/content-section'
import {
    PINNED_DOMAIN_IDS_PREFERENCE_PATH,
    DomainsSection,
    pinnedDomainIdsFrom,
} from './sections/domains/domains-section'
import { DOMAINS_TABLE_CLASS_NAME, DOMAINS_TABLE_SCROLL, domainsColumnFrames } from './sections/domains/domains-table'
import { DOMAINS_TABLE_ROW_HEIGHT } from './components/table-row-heights'
import { buildHeadlineTiles, currenciesIn } from './sections/headline/headline-model'
import { HeadlineSection } from './sections/headline/headline-section'
import { DashboardTemplate, TEMPLATE_SLOTS, templateFor } from './template'

/** Which organization's template applies is only known once its domains are, so the loading page uses the plain one. */
const LOADING_TEMPLATE: DashboardTemplate = 'publisher'

const noop = () => {}

type Registry = ReturnType<typeof useOrgDashboardRegistry>

type LoadedPageProps = {
    orgId: number
    org: GetAccountResponse
    domains: DashboardDomain[]
    registry: Registry
    /** Reads the organization-wide inputs that live above this component again. */
    refreshSharedInputs: () => void
    sharedInputsLoading: boolean
}

/**
 * Everything that depends on the range. It mounts only once the organization's domains are known, because the
 * range's last day is the latest date every domain has finished and so comes from their time zones: nothing is
 * read for a provisional range, and a domain filter in the address is applied from the first render.
 */
const OrgDashboardLoadedPage: React.FC<LoadedPageProps> = ({
    orgId,
    org,
    domains,
    registry,
    refreshSharedInputs,
    sharedInputsLoading,
}) => {
    const template = templateFor(domains)
    const appState = useService(AppState)
    const userPreferences = useUserPreferences(appState.currentUser?.id, undefined, orgId)
    const pinnedDomainIds = pinnedDomainIdsFrom(userPreferences.get(PINNED_DOMAIN_IDS_PREFERENCE_PATH, []))

    const latestCompleteDate = latestCompleteDateAcross(domains.map((domain) => domain.timezone))
    const [query, queryActions] = useOrgDashboardQuery(
        domains.map((domain) => domain.id),
        latestCompleteDate,
    )
    const registryCapabilities = { notifications: registry.notifications, prompts: registry.prompts }
    const series = useOrgDashboardSeries(orgId, query.range, template, registryCapabilities)
    const [notificationRanking, setNotificationRanking] = useState<NotificationRankingChoice>('primary')
    const mixedCurrency = currenciesIn(domains, query.domainIds).length > 1
    const rankings = useOrgDashboardRankings(
        orgId,
        query.range,
        query.domainIds,
        template,
        notificationRanking,
        mixedCurrency,
        registry.notifications,
    )

    // Domains with rows but none described would be a failed domain read, which the account service reports as empty.
    const domainsMissing = series.status === 'ready' && series.rows.length > 0 && domains.length === 0
    const headlineStatus =
        series.status === 'error' || domainsMissing
            ? 'error'
            : series.status === 'loading' || registry.status === 'loading'
              ? 'loading'
              : 'ready'
    const headlineTiles = useMemo(
        () =>
            headlineStatus === 'ready'
                ? buildHeadlineTiles({
                      template,
                      rows: series.rows,
                      domains,
                      selectedDomainIds: query.domainIds,
                      range: query.range,
                      rangeKey: query.rangeKey,
                      subscriberHistory: series.subscriberHistory,
                      registry: {
                          notifications: registry.notifications,
                          prompts: registry.prompts,
                          goodDirectionFor: registry.goodDirectionFor,
                      },
                  })
                : [],
        [
            headlineStatus,
            template,
            series.rows,
            series.subscriberHistory,
            domains,
            query.domainIds,
            query.range,
            query.rangeKey,
            registry.goodDirectionFor,
        ],
    )

    // Without definitions the series and the ranking table are not read, and the sections that show them are hidden,
    // so neither is awaited.
    const definitionsUnavailable = registry.status === 'unavailable'
    const anySectionLoading =
        sharedInputsLoading ||
        (!definitionsUnavailable && (series.status === 'loading' || rankings.status === 'loading'))
    const refreshEverySection = () => {
        refreshSharedInputs()
        series.retry()
        rankings.retry()
    }

    const controls = (
        <div className="org-dashboard-controls">
            <DateRangeControl
                range={query.range}
                rangeKey={query.rangeKey}
                latestCompleteDate={latestCompleteDate}
                onSelectPreset={queryActions.setRange}
                onSelectCustomRange={queryActions.setCustomRange}
            />
            <DomainFilterControl
                domains={domains}
                selectedDomainIds={query.domainIds}
                pinnedDomainIds={pinnedDomainIds}
                onCommit={queryActions.setDomainIds}
            />
            <RefreshButton loading={anySectionLoading} onRefresh={refreshEverySection} />
        </div>
    )

    return (
        <div className="org-dashboard page" data-template={template}>
            <LoadingAnnouncement loading={anySectionLoading} />
            <Flex vertical gap={VERTICAL_WELL_GAP}>
                <PageHeader
                    title="Dashboard"
                    append={
                        <>
                            {`${org.name} · `}
                            <ComparisonSummary
                                range={query.range}
                                rangeKey={query.rangeKey}
                                definitionsCachedAt={registry.cachedAt}
                            />
                        </>
                    }
                    action={controls}
                />
                {definitionsUnavailable && (
                    <Alert
                        className="org-dashboard-registry-alert"
                        type="warning"
                        showIcon
                        title="Metric definitions are unavailable"
                        description="Headline, domain and content figures are hidden until definitions load, so no number is shown without its label and denominator."
                        action={
                            <Button size="small" onClick={registry.retry}>
                                Try Again
                            </Button>
                        }
                    />
                )}
                {!definitionsUnavailable && (
                    <>
                        <HeadlineSection status={headlineStatus} tiles={headlineTiles} onRetry={series.retry} />
                        <DomainsSection
                            orgId={orgId}
                            template={template}
                            status={headlineStatus}
                            rows={series.rows}
                            domains={domains}
                            selectedDomainIds={query.domainIds}
                            range={query.range}
                            rangeKey={query.rangeKey}
                            goodDirectionFor={registry.goodDirectionFor}
                            onRetry={series.retry}
                        />
                        <ContentSection
                            template={template}
                            rankings={rankings}
                            domains={domains}
                            range={query.range}
                            mixedCurrency={mixedCurrency}
                            notificationRanking={notificationRanking}
                            onNotificationRankingChange={setNotificationRanking}
                            onRetry={rankings.retry}
                            topNotificationsWithheld={isTopNotificationsWithheld(template, mixedCurrency, registry)}
                        />
                        <AcquisitionSection
                            status={headlineStatus}
                            rows={series.rows}
                            domains={domains}
                            selectedDomainIds={query.domainIds}
                            range={query.range}
                            goodDirectionFor={registry.goodDirectionFor}
                            onRetry={series.retry}
                        />
                    </>
                )}
            </Flex>
        </div>
    )
}

export const OrgDashboardPage: React.FC<{ orgId: number; org: GetAccountResponse }> = ({ orgId, org }) => {
    const domains = useOrgDashboardDomains(orgId)
    const registry = useOrgDashboardRegistry(orgId)
    const domainsKnown = domains.status === 'ready' || domains.domains.length > 0

    if (domainsKnown) {
        return (
            <OrgDashboardLoadedPage
                orgId={orgId}
                org={org}
                domains={domains.domains}
                registry={registry}
                refreshSharedInputs={() => {
                    domains.retry()
                    registry.retry()
                }}
                sharedInputsLoading={domains.status === 'loading' || registry.status === 'loading'}
            />
        )
    }

    return (
        <div className="org-dashboard page" data-template={LOADING_TEMPLATE}>
            <LoadingAnnouncement loading={domains.status !== 'error'} />
            <Flex vertical gap={VERTICAL_WELL_GAP}>
                <PageHeader title="Dashboard" append={org.name} />
                {domains.status === 'error' ? (
                    <SectionError sectionName="Dashboard" onRetry={domains.retry} />
                ) : (
                    <>
                        <HeadlineSection status="loading" tiles={[]} onRetry={domains.retry} />
                        <TableSectionWell sectionKey="domains">
                            <DashboardTableCard
                                sectionKey="domains"
                                title="Domains"
                                filters={<DomainSearch searchTerm="" onSearchTermChange={noop} disabled />}
                            >
                                <TableSkeleton
                                    columns={domainsColumnFrames(LOADING_TEMPLATE, {
                                        sort: {
                                            column: TEMPLATE_SLOTS[LOADING_TEMPLATE].defaultSort,
                                            direction: 'desc',
                                        },
                                        mixedCurrency: false,
                                    })}
                                    shapes={{ domain: 'two-lines' }}
                                    rowCount={TABLE_PLACEHOLDER_ROW_COUNT}
                                    rowHeight={DOMAINS_TABLE_ROW_HEIGHT}
                                    className={DOMAINS_TABLE_CLASS_NAME}
                                    scroll={DOMAINS_TABLE_SCROLL}
                                />
                            </DashboardTableCard>
                        </TableSectionWell>
                    </>
                )}
            </Flex>
        </div>
    )
}
